import Vue from 'vue'

import { EventService } from 'base/services/EventService'

import { PermutatorService } from '../permutator'
import { ResizeService } from '../resize'
import { DragDropAction } from './DragDropAction'
import { DragDropTarget } from './DragDropTarget'
import { DragDropCoords, DragDropOperationStatus, DragDropTargetBoundaries } from './DragDropTypes'

class DragDropServiceProvider {
  protected isDragging: boolean = false

  protected onScrollHandler

  protected allDragDropTargets: DragDropTarget[] = []
  protected registeredDragTargets = {}
  protected registeredDragActions: DragDropAction[] = []

  protected targetsForCurrentDragOperation: DragDropTarget[] = []
  protected targetsBoundaryMap: DragDropTargetBoundaries[] = []

  protected enteredDragTarget: DragDropTarget | null = null
  protected lastValidDragTarget: DragDropTarget | null = null

  protected currentDragAction: DragDropAction

  protected activeContainerUUID: string
  protected activeContainerIsRoot: boolean = false
  protected containerCandidateUUID: string
  protected containerHoverTimerHandle: number

  constructor() {
    this.onScrollHandler = this.onScroll.bind(this)
    EventService.$on('drag-drop-container-enter', this.onContainerEnter.bind(this))
    EventService.$on('drag-drop-container-leave', this.onContainerLeave.bind(this))
  }

  protected onContainerEnter(containerUUID: string) {
    if (this.containerCandidateUUID) {
      this.onContainerLeave()
    }

    this.containerCandidateUUID = containerUUID

    this.containerHoverTimerHandle = window.setTimeout(() => {
      this.containerCandidateUUID = ''
      this.containerHoverTimerHandle = 0
      this.onContainerSelect(containerUUID)
    }, 1000)
  }

  protected onContainerLeave() {
    if (!this.containerHoverTimerHandle) {
      return
    }
    window.clearTimeout(this.containerHoverTimerHandle)
    this.containerHoverTimerHandle = 0
    this.containerCandidateUUID = ''
  }

  protected onContainerSelect(containerUUID: string) {
    this.selectContainer(containerUUID)
    this.onDrag(this.currentDragAction)
    Vue.nextTick(() => this.reinitialise())
  }

  protected reinitialise() {
    this.initialiseDragOperation(this.targetsForCurrentDragOperation)
    EventService.$emit('drag-drop-service-reinitialise')
  }

  protected onScroll() {
    if (!this.isDragging) {
      return
    }
    this.reinitialise()
  }

  protected getPossibleDragTargets(widgetType): DragDropTarget[] {
    let targets: any[] = []

    // add targets registered for the specific widget type
    if (this.registeredDragTargets.hasOwnProperty(widgetType)) {
      targets = targets.concat(this.registeredDragTargets[widgetType])
    }

    // add generic targets
    if (this.registeredDragTargets.hasOwnProperty('*')) {
      targets = targets.concat(this.registeredDragTargets['*'])
    }

    // remove duplicates
    targets = targets.filter((item, index) => targets.indexOf(item) === index)

    const activeContainerUUID = this.activeContainerUUID

    // remove targets that are not part of the selected container
    return targets.filter((item) => {
      const container = item.getContainer()
      return container ? container.wbUUID === activeContainerUUID : true
    })
  }

  protected getDragTargetAtCoords(x: number, y: number): DragDropTarget | null {
    let boundaries = this.targetsBoundaryMap
    let result

    boundaries.forEach(rect => {
      if (
        ((x >= rect.left) && (x <= rect.right)) &&
        ((y >= rect.top) && (y <= rect.bottom))
      ) {
        result = rect.item
      }
    })

    if (result) {
      return result
    }

    return null
  }

  registerDragTarget(target: DragDropTarget) {
    if (this.allDragDropTargets.indexOf(target) === -1) {
      this.allDragDropTargets.push(target)
    }

    let allowedWidgetTypes = target.getAllowedWidgetTypes()

    allowedWidgetTypes.forEach(widgetType => {
      if (!this.registeredDragTargets.hasOwnProperty(widgetType)) {
        this.registeredDragTargets[widgetType] = []
      }
      this.registeredDragTargets[widgetType].push(target)
    })
  }

  unregisterDragTarget(target: DragDropTarget) {
    let index = this.allDragDropTargets.indexOf(target)

    if (index > -1) {
      this.allDragDropTargets.splice(index, 1)
    }

    let allowedWidgetTypes = target.getAllowedWidgetTypes()

    allowedWidgetTypes.forEach(widgetType => {
      if (!this.registeredDragTargets.hasOwnProperty(widgetType)) {
        return
      }

      index = this.registeredDragTargets[widgetType].indexOf(target)

      if (index > -1) {
        this.registeredDragTargets[widgetType].splice(index, 1)
      }
    })
  }

  unregisterDragAction(dragAction: DragDropAction) {
    let index = this.registeredDragActions.indexOf(dragAction)

    if (index < 0) {
      return
    }

    dragAction.stop()

    this.registeredDragActions.splice(index, 1)
  }

  registerDragAction(dragAction: DragDropAction) {
    if (this.registeredDragActions.includes(dragAction)) {
      return
    }

    dragAction.onDragStart(this.onDragStart.bind(this))
    dragAction.onDragEnd(this.onDragEnd.bind(this))
    dragAction.onDrag(this.onDrag.bind(this))
    dragAction.listen()

    this.registeredDragActions.push(dragAction)
  }

  protected getTargetItemBoundaries(target: DragDropTarget): DragDropTargetBoundaries {
    let rect = target.getTargetElement().getBoundingClientRect() as unknown as DragDropTargetBoundaries
    rect.item = target
    return rect
  }

  protected initialiseDragOperation(targets: DragDropTarget[]) {
    let lateInitializers: DragDropTarget[] = []
    this.targetsBoundaryMap = []

    targets.forEach(target => {
      if (target.getIsLateInitializer()) {
        lateInitializers.push(target)
        return
      }
      this.targetsBoundaryMap.push(this.getTargetItemBoundaries(target))
    })

    if (lateInitializers.length) {
      Vue.nextTick(() => lateInitializers.forEach(target => this.targetsBoundaryMap.push(this.getTargetItemBoundaries(target))))
    }
  }

  protected getInitialDragTarget(action: DragDropAction): DragDropTarget | null {
    const widget = action.getWidget()
    const row = PermutatorService.getParentRow(widget.wbUUID!)

    if (!row) {
      return null
    }

    const parentRowUUID = row.wbUUID
    const targets = this.allDragDropTargets
    const numTargets = targets.length

    let initialDragTarget

    let i = 0
    for (; i < numTargets; i++) {
      const target = targets[i]
      const node = target.getTargetElement()

      if (!node || (node.getAttribute('data-wbuuid') !== parentRowUUID)) {
        continue
      }

      initialDragTarget = target
      break
    }

    return initialDragTarget ? initialDragTarget : null
  }

  onDragStart(action: DragDropAction) {
    this.currentDragAction = action
    this.isDragging = true
    window.addEventListener('scroll', this.onScrollHandler, { passive: true })

    EventService.$emit('drag-drop-service-start-drag', action)

    let targets = this.targetsForCurrentDragOperation = this.getPossibleDragTargets(action.getWidgetType())
    this.initialiseDragOperation(targets)
  }

  onDragEnd(action: DragDropAction) {
    this.isDragging = false
    window.removeEventListener('scroll', this.onScrollHandler)

    let dropTarget = this.enteredDragTarget || this.lastValidDragTarget

    if (dropTarget) {
      dropTarget.drop(action)
    }

    this.targetsBoundaryMap = []
    this.enteredDragTarget = null

    EventService.$emit('drag-drop-service-end-drag', action, dropTarget)
  }

  onDrag(action: DragDropAction, isInitialCall = false) {
    let coords = action.getAbsolutePosition()
    let target = this.getDragTargetAtCoords(coords.x, coords.y)

    // drag operations can be started with the cursor outside the drag element (e.g. u can drag a container grabbing
    // it's breadcrumb navigation). In this case getDragTargetAtCoords() will return null and we need to determine
    // the initial drag target differently (it's necessary to have a drag target at the beginning of a drag&drop
    // operation to have the drag element replaced with the placeholder)
    if (!target && isInitialCall) {
      target = this.getInitialDragTarget(action)
    }

    if (target === this.enteredDragTarget) {
      if (target !== null) {
        target.dragOver(action)
      }
      return
    }

    if (this.enteredDragTarget !== null) {
      this.enteredDragTarget.leave(action)
    }

    this.enteredDragTarget = target

    if (target !== null) {
      target.enter(action)

      // if a target forces an exact drop it will only be triggered when the item is dropped exactly within its bounds
      // therefore we will not store it for a lazy selection during 'onDragEnd'
      if (!target.getForceExactDrop()) {
        this.lastValidDragTarget = target
      }
    }

    this.checkForContainerSwitch(coords, action)
  }

  protected checkForContainerSwitch(coords: DragDropCoords, action: DragDropAction) {
    if (this.activeContainerIsRoot) {
      return
    }

    const containerNode = ResizeService.getNodeByUUID(this.activeContainerUUID)

    if (!containerNode) {
      return
    }

    let rect = containerNode.getBoundingClientRect()
    let x = coords.x
    let y = coords.y
    const isWithinContainer = ((x >= rect.left) && (x <= rect.right)) && ((y >= rect.top) && (y <= rect.bottom))

    if (isWithinContainer) {
      return
    }

    // this will force recalculation of target boundaries map
    this.onContainerSelect(PermutatorService.getRootContainerUUID())

    // we check for a valid target at the current coords
    const target = this.getDragTargetAtCoords(coords.x, coords.y)

    // and leave if we found one
    if (target) {
      return
    }

    // at this point the user moved the widget outside a valid row
    // we will now evaluate the row the container is located in and will simulate a drag within that row
    const row = PermutatorService.getRowContainingPlaceholder()

    if (!row) {
      return
    }

    const rowNode = ResizeService.getNodeByUUID(row.wbUUID!)

    if (!rowNode) {
      return
    }

    rect = rowNode.getBoundingClientRect()

    if (x < rect.left) {
      x = rect.left
    }
    if (x > rect.right) {
      x = rect.right
    }
    if (y < rect.top) {
      y = rect.top
    }
    if (y > rect.bottom) {
      y = rect.bottom
    }

    action.setCoords(x, y)

    this.onDrag(action)
  }

  selectContainer(containerUUID: string) {
    this.activeContainerUUID = containerUUID
    this.activeContainerIsRoot = PermutatorService.isRootContainer(containerUUID)

    if (!this.isDragging) {
      return
    }

    let targets = this.targetsForCurrentDragOperation = this.getPossibleDragTargets(this.currentDragAction.getWidgetType())
    this.initialiseDragOperation(targets)
  }

  getDragDropStatus(): DragDropOperationStatus {
    return this.isDragging ? DragDropOperationStatus.dragging : DragDropOperationStatus.idle
  }
}

export const DragDropService = new DragDropServiceProvider()
